<script lang="ts">
  import { locale, _ } from "svelte-i18n";
  import InputField from "$lib/components/InputField/InputField.svelte";
  import Container from "$lib/components/Container/Container.svelte";
  import { goto } from "$app/navigation";
  import { page as pageStore } from "$app/stores";
  import type { Readable } from "svelte/store";
  import type { Page } from "@sveltejs/kit";
  import { createEventDispatcher } from "svelte";
  import SearchBanner from "$lib/components/SearchBanner/SearchBanner.svelte";
  import { search } from "$lib/stores/search";
  import cfg from "$lib/config/config";
  import { linkTo } from "$lib/utils/url";

  import { SearchDoc } from "$lib/gql/genNostoSearch/codegen";
  import type { SearchQuery } from "$lib/gql/genNostoSearch/codegen";
  import client from "$lib/gql/apolloClient";
  import { recommendations } from "$lib/stores/recommendations";
  import {
    transformNostoSearchCard,
    transformNostoSearchResults,
  } from "$lib/utils/transformNostoSearch";
  import type { CardConnection } from "$lib/gql/genCms/codegen";
  import { searchTerm as searchTermStore } from "$lib/stores/searchTerm";
  import { fade } from "svelte/transition";
  import { onMount, onDestroy } from "svelte";

  let page: Readable<Page> = pageStore;

  const sessionParams = $recommendations?.sessionParams;

  let viewportWidth: number;
  let isMobile = false;
  let mdWidth = 768;

  let lang = $locale;
  lang = lang && lang.split("-")[0];

  export let searchTerm = $searchTermStore
    ? $searchTermStore
    : page && $page?.url?.searchParams.get("term")
      ? $page.url.searchParams.get("term").toString()
      : "";

  $: searchTermStore.set(searchTerm);

  const dispatch = createEventDispatcher();

  let wasEnterPressed = false;

  const closeSearch = () => {
    $search = false;
    dropdownOpen = false;
  };

  const handleKeyDown = event => {
    if (event.key === "Enter" && isMobile) {
      wasEnterPressed = true;
    }
  };

  $: console.log({ wasEnterPressed });
  const handleSearch = event => {
    isMobile && event.preventDefault();
    if (isMobile && wasEnterPressed) {
      wasEnterPressed = false;
      const searchInput = document.getElementById("search");
      if (searchInput) {
        searchInput.blur();
      }
      return;
    }

    dispatch("search", $searchTermStore.trim());
    if ($searchTermStore.trim()) {
      goto(linkTo(`search?term=${$searchTermStore}`));
      closeSearch();
    }
  };

  let querySuggestions = [];
  let productSuggestions: CardConnection;
  let loading = false;
  $: redirect = undefined;

  let debounceTimer;

  const handleSearchSuggestion = async (searchTerm: string) => {
    clearTimeout(debounceTimer);

    if (!searchTerm) {
      querySuggestions = [];
      productSuggestions = undefined;
      redirect = undefined;
      return;
    }

    debounceTimer = setTimeout(async () => {
      loading = true;
      querySuggestions = [];
      productSuggestions = undefined;

      const result = await client.query<SearchQuery>({
        query: SearchDoc,
        variables: {
          accountId: cfg.NOSTO_ACCOUNT_ID,
          query: searchTerm,
          products: {
            size: isMobile ? 4 : 8,
          },
          keywords: { size: isMobile ? 3 : 6 },
          ...(sessionParams && { sessionParams }),
        },
        context: {
          clientName: "nosto-search",
        },
        errorPolicy: "ignore",
      });

      const regex =
        /^(https?:\/\/)?(www\.)?belstaff\.com(\/uk|\/de|\/eu|\/us|\/row)?(\/en|\/es|\/fr|\/de|\/it)?/g;

      if (result?.data?.search?.redirect) {
        const match = result.data.search.redirect.match(regex);
        if (match && match[0]) {
          const matchedBase = match[0];
          const path = result.data.search.redirect.substring(
            matchedBase.length,
          );

          redirect = path;
        }
      }

      querySuggestions = result.data.search.keywords?.hits
        ?.map(hit => {
          return {
            text: hit.keyword,
            display: hit._highlight.keyword,
            count: hit.total,
          };
        })
        .filter(hit => hit.count > 0);

      const transformedResults = transformNostoSearchResults(result, lang);

      productSuggestions = transformNostoSearchCard(transformedResults);

      loading = false;
    }, 300); // 300ms delay
  };

  let dropdownOpen = false;

  let searchContainer: HTMLDivElement;

  $: if (querySuggestions?.length > 0 || productSuggestions?.totalCount > 0)
    dropdownOpen = true;

  $: handleSearchSuggestion($searchTermStore), $searchTermStore;

  $: isMobile = viewportWidth < mdWidth;

  let searchBannerRef: HTMLDivElement;

  const handleScroll = event => {
    if (!isMobile) return;

    if (searchBannerRef && searchBannerRef.contains(event.target)) {
      return;
    }

    if (dropdownOpen) {
      closeSearch();
    }
  };

  const handleClickOutside = event => {
    if (!isMobile) return;

    if (searchContainer && !searchContainer.contains(event.target)) {
      closeSearch();
    }
  };

  onMount(() => {
    window.addEventListener("scroll", handleScroll, true);

    requestAnimationFrame(() => {
      document.addEventListener("click", handleClickOutside);
    });
  });

  onDestroy(() => {
    window.removeEventListener("scroll", handleScroll, true);
    document.removeEventListener("click", handleClickOutside);
  });
</script>

<style lang="scss">
  .search {
    &--container {
      background-color: $surface;
      position: absolute;
      width: 100%;
      height: 43px;
    }

    &--wrapper {
      position: relative;
    }

    &--bg {
      max-width: $container-max;
      background-color: $on-surface-30;
      position: absolute;
      height: calc(100vh);
      top: 100%;
      width: 100%;
      z-index: -1;
    }
  }

  form {
    background-color: $surface;
  }
</style>

<svelte:window bind:innerWidth={viewportWidth} />

<div class="search--container" bind:this={searchContainer}>
  <Container>
    <form on:submit|preventDefault={e => handleSearch(e)}>
      <div class="search--wrapper">
        <InputField
          id="search"
          type="search"
          bind:value={$searchTermStore}
          name="search"
          placeholder={$_("Search")}
          theme="dark"
          fullWidth={true}
          autocomplete="off"
          on:focus
          on:keydown={handleKeyDown}
          on:mouseover={() => (dropdownOpen = true)}
          {loading}
          enterkeyhint="search"
        />
        {#if (querySuggestions?.length > 0 || productSuggestions?.totalCount > 0 || redirect) && $searchTermStore}
          <div transition:fade={{ duration: 300 }} bind:this={searchBannerRef}>
            <SearchBanner
              open={dropdownOpen}
              {querySuggestions}
              {productSuggestions}
              searchTerm={$searchTermStore}
              on:mouseover={() => (dropdownOpen = true)}
              {closeSearch}
              {redirect}
            />
          </div>
        {/if}
      </div>
    </form>
  </Container>
</div>

<!-- svelte-ignore a11y-no-static-element-interactions -->
{#if dropdownOpen && isMobile}
  <div
    class="search--bg"
    on:click={closeSearch}
    on:keydown={closeSearch}
    transition:fade={{ duration: 200 }}
  />
{/if}
